import type { BasketOrder } from './types';

/**
 * Calculates the total weight of all orders in the basket
 * @param basket Array of basket orders
 * @returns Total weight as a percentage
 */
export function calculateTotalWeight(basket: BasketOrder[]): number {
    return basket.reduce((sum, order) => sum + (order.weight ?? 0), 0);
}

/**
 * Checks if the total weight of basket orders sums to exactly 100%
 * Uses a very small tolerance (0.0001) for floating point comparison
 * @param basket Array of basket orders
 * @returns true if weights sum to 100% within tolerance
 */
export function isWeightSumValid(basket: BasketOrder[]): boolean {
    const total = calculateTotalWeight(basket);
    const tolerance = 0.0001;

    return Math.abs(total - 100) <= tolerance;
}

/**
 * Gets the appropriate price for calculating order size based on side
 * - For buy orders: use bid price
 * - For sell orders: use ask price
 * @param order The basket order
 * @returns The price to use, or undefined if not available
 */
export function getPriceForSide(order: BasketOrder): number | undefined {
    if (order.side === 'buy') {
        return order.bidPrice?.value;
    } else if (order.side === 'sell' || order.side === 'sell-short') {
        return order.askPrice?.value;
    }
    return undefined;
}

/**
 * Calculates the number of shares for a notional basket order using NET method
 * Net method: Calculate shares directly from the allocated dollar amount
 * Formula: shares = floor(allocatedAmount / price)
 *
 * @param allocatedAmount The dollar amount allocated to this order (notionalAmount * weight%)
 * @param price The price per share (bid for buy, ask for sell)
 * @returns Number of shares, or 0 if price is not available or invalid
 */
export function calculateNetShares(allocatedAmount: number, price: number | undefined): number {
    if (!price || price <= 0 || !allocatedAmount || allocatedAmount <= 0) {
        return 0;
    }
    return Math.floor(allocatedAmount / price);
}

/**
 * Calculates the number of shares for a notional basket order using GROSS method
 * Gross method: Calculate shares ensuring the total cost doesn't exceed allocated amount
 * This accounts for potential rounding issues and ensures we stay within budget
 * Formula: shares = floor(allocatedAmount / price)
 * Then verify: shares * price <= allocatedAmount
 *
 * @param allocatedAmount The dollar amount allocated to this order (notionalAmount * weight%)
 * @param price The price per share (bid for buy, ask for sell)
 * @returns Number of shares, or 0 if price is not available or invalid
 */
export function calculateGrossShares(allocatedAmount: number, price: number | undefined): number {
    if (!price || price <= 0 || !allocatedAmount || allocatedAmount <= 0) {
        return 0;
    }

    const shares = Math.floor(allocatedAmount / price);

    // Verify we don't exceed the allocated amount
    if (shares * price > allocatedAmount) {
        return Math.max(0, shares - 1);
    }

    return shares;
}

/**
 * Calculates the quantity (number of shares) for a notional basket order
 * @param order The basket order
 * @param notionalAmount The total notional amount for the basket
 * @param notionalType Whether to use 'net' or 'gross' calculation method
 * @returns The calculated quantity (number of shares)
 */
export function calculateNotionalQuantity(
    order: BasketOrder,
    notionalAmount: number | undefined,
    notionalType: 'net' | 'gross' = 'net',
): number {
    // If no notional amount, return 0
    if (!notionalAmount || notionalAmount <= 0) {
        return 0;
    }

    // If no weight, return 0
    const weight = order.weight ?? 0;
    if (weight <= 0) {
        return 0;
    }

    // Calculate the allocated dollar amount for this order
    const allocatedAmount = (notionalAmount * weight) / 100;

    // Get the appropriate price based on side
    const price = getPriceForSide(order);

    // Calculate shares based on method
    if (notionalType === 'gross') {
        return calculateGrossShares(allocatedAmount, price);
    } else {
        return calculateNetShares(allocatedAmount, price);
    }
}
